// Sugere o próximo time balanceado a partir da fila — versão PURA e determinística
// do núcleo do generateTeam (sem os modais interativos de ajuste de gênero/setter).
// Retorna { targetTeamId, playerIds }.

const getPlayerPoints = (p) => p.totalPoints ?? (p.wins - p.losses);

const getCombinations = (array, size) => {
  const result = [];
  const combine = (start, combo) => {
    if (combo.length === size) { result.push([...combo]); return; }
    for (let i = start; i < array.length; i += 1) {
      combo.push(array[i]);
      combine(i + 1, combo);
      combo.pop();
    }
  };
  combine(0, []);
  return result;
};

const findBestCombination = (candidates, slotsNeeded, baseScore, targetScore) => {
  if (candidates.length <= slotsNeeded) return candidates;
  const combinations = getCombinations(candidates, slotsNeeded);
  let bestCombo = combinations[0];
  let bestDiff = Infinity;
  combinations.forEach((combo) => {
    const comboScore = combo.reduce((sum, p) => sum + getPlayerPoints(p), 0);
    const diff = Math.abs((baseScore + comboScore) - targetScore);
    if (diff < bestDiff) { bestDiff = diff; bestCombo = combo; }
  });
  return bestCombo;
};

export function suggestNextTeam({
  players,
  playersPerTeam,
  genderBalanceMode = false,
  genderRestrictedMode = false,
  femalesPerTeam = 1,
  autoBalanceMode = true,
}) {
  const teamAPlayers = players.filter((p) => p.teamId === 1);
  const teamBPlayers = players.filter((p) => p.teamId === 2);
  const aLen = teamAPlayers.length;
  const bLen = teamBPlayers.length;
  const targetTeamId = aLen <= bLen ? 1 : 2;

  const currentTargetTeam = targetTeamId === 1 ? teamAPlayers : teamBPlayers;
  const opposingTeam = targetTeamId === 1 ? teamBPlayers : teamAPlayers;
  const opposingTeamScore = opposingTeam.reduce((acc, p) => acc + getPlayerPoints(p), 0);

  const playersMissing = playersPerTeam - currentTargetTeam.length;
  if (playersMissing <= 0) return { targetTeamId, playerIds: [] };

  const playersWaiting = players.filter((p) => p.teamId === 0 && p.checkedIn);
  const currentFemaleCount = currentTargetTeam.filter((p) => p.gender === 'F').length;
  const femalesNeeded = genderBalanceMode ? Math.max(0, femalesPerTeam - currentFemaleCount) : 0;
  const initialTeamScore = currentTargetTeam.reduce((acc, p) => acc + getPlayerPoints(p), 0);

  const groupedByWaiting = playersWaiting
    .slice()
    .sort((a, b) => (b.waitingFor - a.waitingFor)
      || ((b.checkedInNow ? 1 : 0) - (a.checkedInNow ? 1 : 0)))
    .reduce((acc, player) => {
      if (acc.length === 0 || acc[acc.length - 1][0].waitingFor !== player.waitingFor) {
        acc.push([player]);
      } else {
        acc[acc.length - 1].push(player);
      }
      return acc;
    }, []);

  // Prioridade de mulher (fura fila) — pega a de maior waitingFor.
  let priorityFemales = [];
  const maxFemalesAllowed = genderRestrictedMode
    ? Math.max(0, femalesPerTeam - currentFemaleCount)
    : femalesNeeded;
  if (genderBalanceMode && femalesNeeded > 0 && maxFemalesAllowed > 0) {
    const waitingFemales = playersWaiting
      .filter((p) => p.gender === 'F')
      .slice()
      .sort((a, b) => {
        if (b.waitingFor !== a.waitingFor) return b.waitingFor - a.waitingFor;
        if (autoBalanceMode) {
          const sA = initialTeamScore + getPlayerPoints(a);
          const sB = initialTeamScore + getPlayerPoints(b);
          return Math.abs(sA - opposingTeamScore) - Math.abs(sB - opposingTeamScore);
        }
        return 0;
      });
    priorityFemales = waitingFemales.slice(0, Math.min(femalesNeeded, maxFemalesAllowed));
  }

  const initialSelected = priorityFemales
    .slice(0, Math.min(priorityFemales.length, playersMissing));
  const slotsForNonPriority = playersMissing - initialSelected.length;

  const selectionState = groupedByWaiting.reduce((state, group) => {
    if (state.selected.length >= slotsForNonPriority) return state;
    const available = group
      .filter((p) => !initialSelected.some((sp) => sp.playerId === p.playerId));
    const slotsLeft = slotsForNonPriority - state.selected.length;
    const baseScore = initialTeamScore
      + initialSelected.reduce((s, p) => s + getPlayerPoints(p), 0)
      + state.selected.reduce((s, p) => s + getPlayerPoints(p), 0);
    const toAdd = autoBalanceMode
      ? findBestCombination(available, slotsLeft, baseScore, opposingTeamScore)
      : available.slice(0, slotsLeft);
    return { selected: [...state.selected, ...toAdd] };
  }, { selected: [] });

  let selected = [...initialSelected, ...selectionState.selected];

  // Fallback: completar (respeitando a fila) se ainda faltar.
  if (selected.length < playersMissing) {
    const chosen = selected.map((p) => p.playerId);
    const remaining = playersWaiting
      .filter((p) => !chosen.includes(p.playerId))
      .sort((a, b) => b.waitingFor - a.waitingFor);
    selected = [...selected, ...remaining.slice(0, playersMissing - selected.length)];
  }

  if (selected.length > playersMissing) selected = selected.slice(0, playersMissing);

  return { targetTeamId, playerIds: selected.map((p) => p.playerId) };
}
